<script setup lang="ts">
import { FormLabel, FormInput, FormCheck } from "../../../base-components/Form";
import LoadingIcon from "../../../base-components/LoadingIcon";
import Button from "../../../base-components/Button";
import Preview from "../../../base-components/Preview";
import { onBeforeMount, ref } from "vue";
import { reactive, toRefs } from "vue";
import { required, helpers } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import { userUserStore } from "../../../stores/user";
import { useMensajesStore } from "../../../services/mensajes";
import {
  collection,
  getDocs,
  getFirestore,
  query,
  where
} from "firebase/firestore";

import TomSelect from "../../../custom-components/TomSelect";
import { useUsuarioRolesStore } from "../../../services/usuarioRoles";
import { useUsuariosInvitacionesStore } from "../../../services/usuariosInvitaciones";
import {
  InvitacionCrear,
  LogAcciones,
  UsuarioSistemaInvitacion
} from "@fixeduy/restaurantestypes";
import { utilFechaConvertirNumeroString } from "../../../utils/fechaFixedTypes";
import { invitacionCrear } from "../../../services/cloudFunctions/invitacionCrear";
import { getCurrentTime } from "../../../utils/fecha";
import { useLogAccionesStore } from "../../../services/logAcciones";

const usuariosSistemasInvitacionesStore = useUsuariosInvitacionesStore();
const userStore = userUserStore();
const logAccionesStore = useLogAccionesStore();
const usuariosRolesStore = useUsuarioRolesStore();
const cargando = ref(false);

onBeforeMount(() => {
  usuariosRolesStore.getRolesFromFirestore();
});

const emit = defineEmits<{
  (e: "cerrar"): void;
}>();

const formData = reactive({
  nombre: "",
  mail: "",
  rol: "Admin",
  vendedor: true,
  vendedorRestringido: false,
  locales: []
});

const rules = {
  nombre: {
    required: helpers.withMessage("El nombre no puede estar vacío", required)
  },
  mail: {
    required: helpers.withMessage("El mail no puede estar vacío", required)
  },
  rol: {
    required: helpers.withMessage("El rol no puede estar vacío", required)
  },
  vendedor: {},
  vendedorRestringido: {},
  locales: {}
};

const validate = useVuelidate(rules, toRefs(formData));
const mensajesStore = useMensajesStore();

const validarExisteUsuario = async (mail: string): Promise<boolean> => {
  const db = getFirestore();
  const collectionRoute = `empresas/${userStore.empresa?.id}/usuarios/`;
  const existe = await getDocs(
    query(collection(db, collectionRoute), where("mail", "==", mail))
  );

  return existe.docs.length > 0;
};

const handlerCreate = async () => {
  const result = await validate.value.$validate();

  if (result) {
    cargando.value = true;
    // valido que no exista una invitación para ese usuario
    const existeInvitacion =
      await usuariosSistemasInvitacionesStore.existeInvitacion(formData.mail);

    if (existeInvitacion) {
      cargando.value = false;
      mensajesStore.mostrarModal(
        "Ya existe invitación",
        "Ya existe una invitación para ese usuario, si lo deseas en Invitaciones a Usuarios, en el + a la derecha puedes reenviar el mail de invitación. Sino también puedes contactarte con soporte mediante el chat de ayuda, el botón celeste abajo a la derecha.",
        mensajesStore.mensajeType.error
      );
    } else {
      const existeUsuario = await validarExisteUsuario(formData.mail);
      if (existeUsuario) {
        cargando.value = false;
        mensajesStore.mostrarModal(
          "Ya existe usuario",
          "Ya existe un usuario con el mail indicado. Recuerda que puedes contactarte con soporte mediante el chat de ayuda, el botón celeste abajo a la derecha.",
          mensajesStore.mensajeType.error
        );
      } else {
        let usuario: UsuarioSistemaInvitacion = {
          id: "",
          mail: formData.mail.toLocaleLowerCase(),
          nombre: formData.nombre,
          enviadaPorNombre: userStore.userLoggedIn!.displayName!,
          enviadaPorMail: userStore.userLoggedIn!.email!,
          enviadaEl: Date.now() / 1000,
          enviadaElString: utilFechaConvertirNumeroString(
            Date.now(),
            true,
            "/"
          ),
          empresaNombreComercial: userStore.empresa?.nombreComercial ?? "",
          empresaRazonSocial: userStore.empresa?.razonSocial ?? "",
          empresaRUT: userStore.empresa?.rut ?? "",
          empresaId: userStore.empresa?.id ?? "",
          rolId: formData.rol
        };
        const params: InvitacionCrear = {
          invitacion: usuario,
          idEmpresa: userStore.empresa?.id ?? ""
        };
        const resultado = await invitacionCrear(params);
        if (resultado.exito) {
          const logAcciones: LogAcciones = {
            id: "",
            accion: [`Se envía invitación a usuario ${usuario.mail}.`],
            modulo: "usuarios",
            fecha: getCurrentTime(),
            usuario: userStore.userLoggedIn?.uid!,
            mail: userStore.userLoggedIn?.email!
          };

          await logAccionesStore.guardarLog(logAcciones);
          mensajesStore.mostrarModal(
            "Invitación enviada",
            "Se ha enviado la invitación al usuario. Recuerda que el usuario deberá aceptar la invitación para poder acceder a Atiendo.",
            mensajesStore.mensajeType.exito
          );
          usuariosSistemasInvitacionesStore.init();
          emit("cerrar");
        } else {
          mensajesStore.mostrarModal(
            "Error al enviar la invitación",
            "Hubo un error al enviar la invitación, por favor intenta nuevamente.",
            mensajesStore.mensajeType.error
          );
        }
      }
    }
  }
};
</script>

<template>
  <Preview class="box mt-5">
    <form class="validate-form p-4" @submit.prevent="handlerCreate">
      <div
        class="border-b sm:flex-row border-slate-200/60 dark:border-darkmode-400"
      >
        <h2 class="mr-auto text-lg font-medium">Agregar usuario con mail</h2>
        <div class="col-span-12 lg:col-span-6">
          <div class="flex mt-5">
            <div class="flex-none w-50 text-right pr-5 mt-2">
              <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
                Nombre
              </FormLabel>
            </div>
            <div class="flex-1">
              <FormInput
                type="text"
                v-model.trim="validate.nombre.$model"
                :class="{
                  'border-danger': validate.nombre.$error
                }"
                placeholder="Nombre"
              />
              <template v-if="validate.nombre.$error">
                <div
                  v-for="(error, index) in validate.nombre.$errors"
                  :key="index"
                  class="mt-2 text-danger"
                >
                  {{ error.$message }}
                </div>
              </template>
            </div>
          </div>

          <div class="flex mt-5">
            <div class="flex-none w-50 text-right pr-5 mt-2">
              <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
                Mail
              </FormLabel>
            </div>
            <div class="flex-1">
              <FormInput
                type="text"
                v-model.trim="validate.mail.$model"
                :class="{
                  'border-danger': validate.mail.$error
                }"
                placeholder="Mail"
              />
              <template v-if="validate.mail.$error">
                <div
                  v-for="(error, index) in validate.mail.$errors"
                  :key="index"
                  class="mt-2 text-danger"
                >
                  {{ error.$message }}
                </div>
              </template>
            </div>
          </div>

          <!-------------------- rol ------------------------>
          <div class="flex mt-5">
            <div class="flex-none w-50 text-right pr-5">
              <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
                Rol
              </FormLabel>
            </div>
            <div class="flex-1">
              <TomSelect
                v-model.trim="formData.rol"
                :class="{
                  'border-danger': validate.rol?.$error
                }"
                style="width: 50%"
                :options="{ placeholder: 'Selecciona el rol' }"
              >
                <template v-for="rol in usuariosRolesStore.roles" :key="rol.id">
                  <option :value="rol.id">{{ rol.rol }}</option>
                </template>
              </TomSelect>
            </div>
          </div>
        </div>
        <p class="mt-5">
          El sistema enviará un mail al usuario para que inicie sesión en
          acceso.atiendo.cloud
        </p>
        <p class="mt-5">
          Recuerda que los usuarios en Atiendo son GRATIS, puedes crear todos
          los que quieras.
        </p>
        <div v-if="!cargando" class="flex mt-6">
          <div class="flex-1 text-left">
            <Button
              variant="secondary"
              class="mb-2 mr-1"
              @click="
                () => {
                  emit('cerrar');
                }
              "
            >
              Cancelar</Button
            >
          </div>
          <div class="flex-1 text-right">
            <Button variant="primary" class="mb-2 mr-1" @click="handlerCreate">
              Agregar nuevo usuario
            </Button>
          </div>
        </div>
        <div v-else class="flex my-6">
          <div class="mx-auto">
            <LoadingIcon icon="oval" class="w-8 h-8" />
          </div>
        </div>
      </div>
    </form>
  </Preview>
</template>
